import { useEffect, useState } from "react";
import styled from "styled-components";
import { ButtonLink, ModalDefault, Text } from "@ficatranquilo/contabilidade.com-ui";

// Parâmetros do player:
// rel=0             -> sugestões do fim restritas ao próprio canal
// modestbranding=1  -> reduz a marca do YouTube nos controles
// iv_load_policy=3  -> sem anotações/cards
// playsinline=1     -> no iOS toca dentro do modal, sem abrir em tela cheia
const PLAYER_PARAMS = "rel=0&modestbranding=1&iv_load_policy=3&playsinline=1";

const PlayIcon = () => (
    <svg width={20} height={20} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
        strokeWidth="1.5" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round"
            d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 0 1 0 1.971l-11.54 6.347a1.125 1.125 0 0 1-1.667-.985V5.653Z" />
    </svg>
);

const Player = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

// 16:9 na largura do modal large da lib (800px, altura automática)
const Embed = styled.div`
    position: relative;
    width: 100%;
    padding-top: 56.25%;

    iframe {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border: 0;
        border-radius: 8px;
    }
`;

const List = styled.ul`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
`;

const Item = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 12px 16px;
    text-align: left;
    background: ${({ theme }) => theme.colors.N0};
    border: 1px solid ${({ theme }) => theme.colors.N100};
    border-radius: 8px;
    cursor: pointer;
    transition: border-color 0.2s;

    svg {
        flex-shrink: 0;
        color: ${({ theme }) => theme.colors.ACTION};
    }

    &:hover {
        border-color: ${({ theme }) => theme.colors.ACTION};
    }
`;

/**
 * Modal com o(s) tutorial(is) em vídeo da tela.
 *
 * Props:
 * - show: boolean
 * - onHide: function
 * - videos: array de { youtubeId, title }. Com um só, abre direto no player;
 *   com mais de um, abre na lista e o player entra depois da escolha.
 */
export default function HelpVideoModal({ show, onHide, videos }) {
    const isSingle = videos.length === 1;
    const [selected, setSelected] = useState(isSingle ? 0 : null);

    // Reabrir o modal sempre volta ao estado inicial, sem lembrar a última escolha
    useEffect(() => {
        if (show) setSelected(isSingle ? 0 : null);
    }, [show, isSingle]);

    // Montado só aberto: ao fechar, o iframe sai e a reprodução para
    if (!show) return null;

    const video = selected === null ? null : videos[selected];

    return (
        <ModalDefault
            $showModal={show}
            $setShowModal={onHide}
            $size={video ? "large" : "medium"}
            $title={video ? video.title : "Tutoriais desta tela"}
            $contentBackground="N0">
            {video ? (
                <Player>
                    <Embed>
                        <iframe
                            key={video.youtubeId}
                            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?${PLAYER_PARAMS}`}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                            referrerPolicy="strict-origin-when-cross-origin"
                            allowFullScreen
                        />
                    </Embed>
                    {!isSingle ? (
                        <div>
                            <ButtonLink $text="Ver os outros tutoriais desta tela" $action={() => setSelected(null)} />
                        </div>
                    ) : null}
                </Player>
            ) : (
                <List>
                    {videos.map((v, index) => (
                        <li key={v.youtubeId}>
                            <Item type="button" onClick={() => setSelected(index)}>
                                <PlayIcon />
                                <Text $text={v.title} $variant="paragraph_small_medium" $color="N900" />
                            </Item>
                        </li>
                    ))}
                </List>
            )}
        </ModalDefault>
    );
}
